import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Badge } from 'react-bootstrap';
import { FiCloudOff, FiRefreshCw, FiEye, FiEdit, FiTrash2 } from 'react-icons/fi';
import { fetchTripsForecastsNotTriggeredRequest, deleteTripForecastNotTriggeredRequest } from '../../store/actions/tripsActions';
import { getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import PartnerCompanyAutocomplete from '../../components/Common/PartnerCompanyAutocomplete';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsForecasts.css';

const EMPTY_TRIPS_FORECASTS_NOT_TRIGGERED_FILTERS = {
    policy_code: '',
    reservation_number: '',
    client_email: '',
    created_date: '',
    company_name: '',
    activity_end_date_from: '',
    activity_end_date_to: '',
};

const DDMMYYYY_FORECASTS_NOT_TRIGGERED_DATE_FILTER_KEYS = new Set([
    'activity_end_date_from',
    'activity_end_date_to',
]);

const TRIPS_FORECASTS_NOT_TRIGGERED_TABLE_COLSPAN = 13;

const AdminTripsForecastsNotTriggered = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const forecastsState = useSelector((state) => state.trips.forecastsNotTriggered);
    const deleteState = useSelector((state) => state.trips.deleteForecastNotTriggered);

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('trips-forecasts-not-triggered', EMPTY_TRIPS_FORECASTS_NOT_TRIGGERED_FILTERS, {
        persistWhen: isAuthenticated,
        withCompanyPartnerSearch: true,
    });
    const [filterPartners, setFilterPartners] = useState([]);
    const [filterPartnersLoading, setFilterPartnersLoading] = useState(false);
    const [filterPartnersError, setFilterPartnersError] = useState(null);

    const partnerCompanyOptions = useMemo(() => {
        const names = filterPartners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [filterPartners]);

    const handleDetails = (item) => {
        const forecastNotTriggeredId = getDeep(item, ['forecast_not_triggered_id']);
        if (forecastNotTriggeredId) {
            navigate(`/admin/trips/forecasts-not-triggered/${forecastNotTriggeredId}`);
        }
    };

    const handleEdit = (item) => {
        console.log('Edit forecast not triggered:', item);
        alert('Fonction éditer à implémenter');
    };

    const handleDelete = (item) => {
        const forecastNotTriggeredId = getDeep(item, ['forecast_not_triggered_id']);
        if (!forecastNotTriggeredId) {
            alert('ID de prévision non trouvé');
            return;
        }

        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette prévision non déclenchée ?')) {
            dispatch(deleteTripForecastNotTriggeredRequest(forecastNotTriggeredId));
        }
    };

    // Charger les prévisions non déclenchées avec pagination et filtres
    const convertDateToDDMMYYYY = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) return dateStr;
        if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        }
        return dateStr;
    };

    const loadForecasts = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        // Utiliser les filtres fournis ou les filtres appliqués
        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        // Ajouter les filtres non vides
        Object.keys(activeFilters).forEach((key) => {
            const value = activeFilters[key];
            if (value == null || String(value).trim() === '') return;
            const trimmed = String(value).trim();
            if (DDMMYYYY_FORECASTS_NOT_TRIGGERED_DATE_FILTER_KEYS.has(key)) {
                params[key] = convertDateToDDMMYYYY(trimmed);
            } else {
                params[key] = trimmed;
            }
        });

        dispatch(fetchTripsForecastsNotTriggeredRequest(params));
    }, [itemsPerPage, appliedFilters, dispatch]);

    useEffect(() => {
        if (isAuthenticated) {
            loadForecasts(currentPage);
        }
        /* eslint-disable-next-line */
    }, [isAuthenticated]);

    useEffect(() => {
        const loadFilterPartners = async () => {
            try {
                setFilterPartnersLoading(true);
                setFilterPartnersError(null);
                const response = await getAllPartners();
                const partnersArray = response?.users && Array.isArray(response.users)
                    ? response.users
                    : (Array.isArray(response) ? response : []);
                setFilterPartners(partnersArray);
            } catch (error) {
                setFilterPartners([]);
                setFilterPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setFilterPartnersLoading(false);
            }
        };
        if (isAuthenticated) {
            loadFilterPartners();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        loadForecasts(currentPage);
    }, [currentPage, loadForecasts]);

    // Recharger la liste après suppression réussie
    useEffect(() => {
        if (deleteState.success) {
            // Masquer le message de succès après 3 secondes
            const timer = setTimeout(() => {
                loadForecasts(currentPage);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [deleteState.success, currentPage, loadForecasts]);

    // Extraire les données et métadonnées de la réponse API
    const forecastsData = forecastsState?.data?.data || forecastsState?.data || [];
    const forecastsArray = Array.isArray(forecastsData) ? forecastsData : [];
    const meta = forecastsState?.data?.meta || {
        total: 0,
        skip: 0,
        limit: itemsPerPage,
        page: currentPage,
        total_pages: 0,
    };

    // Pagination côté serveur - utiliser les métadonnées de l'API
    const totalPages = meta.total_pages || Math.max(1, Math.ceil(meta.total / itemsPerPage));
    const totalItems = meta.total || 0;
    const indexOfFirstItem = meta.skip || 0;
    const indexOfLastItem = Math.min(indexOfFirstItem + forecastsArray.length, totalItems);

    const handleRefresh = () => {
        loadForecasts(currentPage);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadForecasts(1, nextFilters);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const emptyFilters = { ...EMPTY_TRIPS_FORECASTS_NOT_TRIGGERED_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadForecasts(1, emptyFilters);
        }, 100);
    };

    // Calculate pagination range (show max 5 pages around current)
    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    // Utiliser directement les prévisions de l'API (pas de filtrage côté client)
    const currentForecasts = forecastsArray;

    const getDeep = (obj, path, fallback = 'N/A') => {
        try {
            const val = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return val ?? fallback;
        } catch {
            return fallback;
        }
    };

    return (
        <div className="dashboard-page admin-trips-forecasts-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiCloudOff className="me-2" />
                                Trip - Gestion Prévisions Non Déclenchées
                            </h2>
                            <p className="text-muted mb-0">Prévisions non déclenchées liées aux séjours</p>
                        </div>
                    </div>

                    {forecastsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {forecastsState.error}
                        </Alert>
                    )}

                    {deleteState.error && (
                        <Alert variant="danger" className="mt-3">
                            {deleteState.error}
                        </Alert>
                    )}

                    {deleteState.success && (
                        <Alert variant="success" className="mt-3" onClose={() => {}} dismissible>
                            Prévision non déclenchée supprimée avec succès.
                        </Alert>
                    )}

                    {/* Filtres avancés */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Filtres de recherche</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    Réinitialiser
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    Appliquer les filtres
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Code police</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Ex: 280007906"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Label>Nom de société</Form.Label>
                                    <PartnerCompanyAutocomplete
                                        value={filters.company_name}
                                        onChange={(companyName) => setFilters((prev) => ({ ...prev, company_name: companyName }))}
                                        onInputChange={setCompanyPartnerSearch}
                                        options={partnerCompanyOptions}
                                        loading={filterPartnersLoading}
                                        error={filterPartnersError}
                                        onKeyDown={handleKeyPress}
                                        placeholder="Rechercher un partenaire…"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Numéro de réservation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Email client</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="client_email"
                                            value={filters.client_email}
                                            onChange={handleFilterChange}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Email du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date de création</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="created_date"
                                            value={filters.created_date}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date fin activité (de)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_from"
                                            value={filters.activity_end_date_from}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-0">
                                        <Form.Label>Date fin activité (à)</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_end_date_to"
                                            value={filters.activity_end_date_to}
                                            onChange={handleFilterChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={forecastsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${forecastsState.loading ? 'spinning' : ''}`} />
                            Actualiser
                        </Button>
                    </div>

                    {forecastsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Chargement des prévisions non déclenchées...</p>
                        </div>
                    ) : forecastsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{forecastsState.error}</Alert>
                        </div>
                    ) : currentForecasts.length === 0 ? (
                        <div className="text-center py-5">
                            <FiCloudOff size={48} className="text-muted mb-3" />
                            <p className="text-muted">Aucune prévision non déclenchée disponible</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Code</th>
                                            <th>Code police</th>
                                            <th>Numéro réservation</th>
                                            <th>Prénom</th>
                                            <th>Nom</th>
                                            <th>E-mail</th>
                                            <th>Partenaire</th>
                                            <th>Date création</th>
                                            <th>Statut</th>
                                            <th>Basé sur</th>
                                            <th>Début</th>
                                            <th>Fin</th>
                                            <th className="text-end col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.isArray(currentForecasts) && currentForecasts.length > 0 ? (
                                            currentForecasts.map((item, idx) => (
                                                <tr key={getDeep(item, ['forecast_not_triggered_id'], idx)}>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['code'])}
                                                    </td>
                                                    <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                                        {getDeep(item, ['policy_info', 'policy_code'])}
                                                    </td>
                                                    <td>{getDeep(item, ['activity_info', 'reservation_number'])}</td>
                                                    <td>{getDeep(item, ['client_info', 'first_name'])}</td>
                                                    <td>{getDeep(item, ['client_info', 'last_name'])}</td>
                                                    <td>{getDeep(item, ['client_info', 'email'])}</td>
                                                    <td>{getDeep(item, ['partner_info', 'company_name'])}</td>
                                                    <td>{getDeep(item, ['created_date'])}</td>
                                                    <td>
                                                        <Badge bg={getDeep(item, ['status'], '') === 'Created' ? 'info' : 'secondary'}>
                                                            {getDeep(item, ['status'])}
                                                        </Badge>
                                                    </td>
                                                    <td>{getDeep(item, ['trigger_based_on'])}</td>
                                                    <td>
                                                        {getDeep(
                                                            item,
                                                            ['activity_info', 'activity_start_date'],
                                                            getDeep(item, ['activity_info', 'trip_effective_start_date'])
                                                        )}
                                                    </td>
                                                    <td>
                                                        {getDeep(
                                                            item,
                                                            ['activity_info', 'activity_end_date'],
                                                            getDeep(item, ['activity_info', 'trip_effective_end_date'])
                                                        )}
                                                    </td>
                                                    <td className="text-end col-actions">
                                                        <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleDetails(item)} title="Détails" aria-label="Détails">
                                                            <FiEye />
                                                        </Button>
                                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEdit(item)} title="Modifier" aria-label="Modifier">
                                                            <FiEdit />
                                                        </Button>
                                                        <Button 
                                                            variant="outline-danger" 
                                                            size="sm" 
                                                            onClick={() => handleDelete(item)} 
                                                            title="Supprimer" 
                                                            aria-label="Supprimer"
                                                            disabled={deleteState.loading}
                                                        >
                                                            <FiTrash2 />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={TRIPS_FORECASTS_NOT_TRIGGERED_TABLE_COLSPAN} className="text-center">
                                                    Aucune prévision non déclenchée trouvée
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {/* Pagination */}
                    {totalItems > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4">
                            <div className="text-muted">
                                Affichage de {indexOfFirstItem + 1} à {indexOfLastItem} sur {totalItems} prévision{totalItems > 1 ? 's' : ''} non déclenchée{totalItems > 1 ? 's' : ''}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First
                                    onClick={() => {
                                        setCurrentPage(1);
                                        loadForecasts(1);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => {
                                        const prevPage = currentPage - 1;
                                        setCurrentPage(prevPage);
                                        loadForecasts(prevPage);
                                    }}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];

                                    // Show first page if not in range
                                    if (startPage > 1) {
                                        pages.push(
                                            <Pagination.Item key={1} onClick={() => {
                                                setCurrentPage(1);
                                                loadForecasts(1);
                                            }}>
                                                1
                                            </Pagination.Item>
                                        );
                                        if (startPage > 2) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-start" />);
                                        }
                                    }

                                    // Show pages in range
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => {
                                                    setCurrentPage(i);
                                                    loadForecasts(i);
                                                }}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }

                                    // Show last page if not in range
                                    if (endPage < totalPages) {
                                        if (endPage < totalPages - 1) {
                                            pages.push(<Pagination.Ellipsis key="ellipsis-end" />);
                                        }
                                        pages.push(
                                            <Pagination.Item key={totalPages} onClick={() => {
                                                setCurrentPage(totalPages);
                                                loadForecasts(totalPages);
                                            }}>
                                                {totalPages}
                                            </Pagination.Item>
                                        );
                                    }

                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => {
                                        const nextPage = currentPage + 1;
                                        setCurrentPage(nextPage);
                                        loadForecasts(nextPage);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => {
                                        setCurrentPage(totalPages);
                                        loadForecasts(totalPages);
                                    }}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsForecastsNotTriggered;

